import { RelativeWallInfo } from '@/editor/data/wall/Wall';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { EPointOnSegmentType, EWallOperateType, IUniformWallData, IOriginModifyWallInfo } from '../../types';
import { Line2d } from '../../math/Line2d';
import { IShape2d, IShapeSeg2d } from '@/editor/types/shape';
import { getShapeSeg, pointOnSeg } from '@/editor/math/shape/other';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { effectiveDistance } from '@/editor/math/const';
import { normalizeAngle } from '@/editor/math/utils';
import { isNumber } from 'lodash-es';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { isArc } from '../arc';

type IOperateData =
  | {
      type: EWallOperateType.StartPoint | EWallOperateType.EndPoint | EWallOperateType.ArcMidPoint;
      point: IPoint2d;
    }
  | {
      type: EWallOperateType.Pan;
      offset: number;
      direction?: IPoint2d;
    };

enum PointDistanceType {
  Near = 'near',
  Far = 'far'
}

interface WallStore {
  getUniformWallData(wallId: string): IUniformWallData | undefined;
  getConnectedWallInfo(wallId: string, skipVirtualWall?: boolean): RelativeWallInfo | undefined;
  // getPointMap(): Map<string, string[]>;
}

/******************************************
  墙体联动规则（考虑三种情况）：
  1. 主墙体端点和其他墙体端点（只有一条）相连；
  2. 主墙体端点在其他墙体上；
  3. 主墙体（除了端点外）上面有其他墙体端点；
******************************************/

export class WallMoveCalculator {
  private _wallStore: WallStore;

  private _mainWallData: IUniformWallData | null = null;
  private _mainSeg: IShapeSeg2d | null = null;
  private _mainMovedSeg: IShapeSeg2d | null = null;
  private _mainMovedShape: IShape2d | null = null;

  private _operateData: IOperateData = {
    type: EWallOperateType.Pan,
    offset: 0
  };

  // private _movedPoint: IPoint2d | null = null;
  private _relativeWallInfo: RelativeWallInfo | null = null;
  // private _pointMap: Map<string, string[]> | null = null;
  private _panDirection: IPoint2d = { x: 0, y: 0 };

  private _result: {
    start?: {
      intersectPoint: IPoint2d;
      wallId: string;
      intersectPointType: EPointOnSegmentType;
    };
    end?: {
      intersectPoint: IPoint2d;
      wallId: string;
      intersectPointType: EPointOnSegmentType;
    };
    center?: IPoint2d;
    clockwise?: boolean;
    modifyWallInfos?: IOriginModifyWallInfo[];
    otherRelativeWallInfos?: {
      wallId: string;
    }[];
  } = {};

  constructor(wallStore: WallStore) {
    this._wallStore = wallStore;
  }

  setMainWall(wallId: string) {
    const wallData = this._wallStore.getUniformWallData(wallId);
    this._mainWallData = wallData || null;
    if (!wallData) return;

    this._mainSeg = isArc(wallData)
      ? new ArcSeg2d({
          start: wallData.start,
          end: wallData.end,
          center: wallData.center,
          clockwise: wallData.clockwise
        })
      : new LineSeg2d(wallData.start, wallData.end);
    this._relativeWallInfo = this._wallStore.getConnectedWallInfo(wallId, false) || null;

    const d = Point2dUtils.getUnitDirection(wallData.start, wallData.end);
    this._panDirection = Point2dUtils.rotateDirection(d, Math.PI / 2);
  }

  /*
   * 平移规则：
   * 端点在主墙体上的墙体，会进行拉伸或压缩；
   * 主墙体端点在相邻墙体上（非端点端点相连）时，主墙体会被裁剪或拉伸；
   */
  compute(operateData: IOperateData) {
    if (!this._mainWallData) return this._result;

    this.prepare(operateData);

    this.computeMoveInfo();

    return this._result;
  }

  private prepare(operateData: IOperateData) {
    // 清空结果数据
    this._result = {};

    if (!this._mainWallData) return;

    this._operateData = operateData;
    const { start, end, center, clockwise } = this._mainWallData;

    if (center && clockwise !== undefined) {
      switch (operateData.type) {
        case EWallOperateType.StartPoint: {
          const currentArc = new ArcSeg2d({ start, end, center, clockwise });
          const arc = new ArcSeg2d({ start: operateData.point, end, sagitta: currentArc.getSagitta() });
          this._result.center = arc.center;
          this._result.clockwise = arc.clockwise;
          this._mainMovedSeg = new ArcSeg2d({
            start: arc.start,
            end: arc.end,
            center: arc.center,
            clockwise: arc.clockwise
          });
          this._mainMovedShape = this._mainMovedSeg.createCircle();
          break;
        }
        case EWallOperateType.EndPoint: {
          const currentArc = new ArcSeg2d({ start, end, center, clockwise });
          const arc = new ArcSeg2d({ start, end: operateData.point, sagitta: currentArc.getSagitta() });
          this._result.center = arc.center;
          this._result.clockwise = arc.clockwise;
          this._mainMovedSeg = new ArcSeg2d({
            start: arc.start,
            end: arc.end,
            center: arc.center,
            clockwise: arc.clockwise
          });
          this._mainMovedShape = this._mainMovedSeg.createCircle();
          break;
        }
        case EWallOperateType.ArcMidPoint: {
          const arc = new ArcSeg2d({ start, end, thirdPoint: operateData.point });
          this._result.center = arc.center;
          this._result.clockwise = arc.clockwise;
          this._mainMovedSeg = new ArcSeg2d({
            start: arc.start,
            end: arc.end,
            center: arc.center,
            clockwise: arc.clockwise
          });
          this._mainMovedShape = this._mainMovedSeg.createCircle();
          break;
        }
        case EWallOperateType.Pan: {
          if (operateData.direction) {
            this._panDirection = operateData.direction;
          }
          const newStart = Point2dUtils.addDistance(start, operateData.offset, this._panDirection);
          const newEnd = Point2dUtils.addDistance(end, operateData.offset, this._panDirection);
          const newCenter = Point2dUtils.addDistance(center, operateData.offset, this._panDirection);
          this._result.center = newCenter;
          this._result.clockwise = clockwise;
          this._mainMovedSeg = new ArcSeg2d({ start: newStart, end: newEnd, center: newCenter, clockwise });
          this._mainMovedShape = this._mainMovedSeg.createCircle();
          break;
        }
      }
    } else {
      switch (operateData.type) {
        case EWallOperateType.StartPoint: {
          const startPoint = operateData.point;
          const endPoint = this._mainWallData.end;
          this._mainMovedSeg = new LineSeg2d(startPoint, endPoint);
          this._mainMovedShape = new Line2d({
            points: { p1: this._mainMovedSeg.start, p2: this._mainMovedSeg.end }
          });
          break;
        }
        case EWallOperateType.EndPoint: {
          const startPoint = this._mainWallData.start;
          const endPoint = operateData.point;
          this._mainMovedSeg = new LineSeg2d(startPoint, endPoint);
          this._mainMovedShape = new Line2d({
            points: { p1: this._mainMovedSeg.start, p2: this._mainMovedSeg.end }
          });
          break;
        }
        case EWallOperateType.Pan: {
          const wallSeg = new LineSeg2d(this._mainWallData.start, this._mainWallData.end);
          this._mainMovedSeg = Curve2dUtils.offsetCurve(wallSeg, -operateData.offset);
          this._mainMovedShape = new Line2d({
            points: { p1: this._mainMovedSeg.start, p2: this._mainMovedSeg.end }
          });
          break;
        }
      }
    }

    // 上面两个 switch 并未覆盖全部组合（如直墙 + ArcMidPoint），算不出移动后的线段时没有默认端点可设
    if (!this._mainMovedSeg) return;

    // 设置默认起点/终点
    this._result.start = {
      intersectPoint: this._mainMovedSeg.start,
      wallId: '',
      intersectPointType: EPointOnSegmentType.Empty
    };
    this._result.end = {
      intersectPoint: this._mainMovedSeg.end,
      wallId: '',
      intersectPointType: EPointOnSegmentType.Empty
    };
  }

  private computeMoveInfo() {
    // if (Math.abs(this._moveDistance) < thresholdValue) return;

    // 计算主墙体移动后的端点，平移时才会进行端点计算
    if (this._operateData.type === EWallOperateType.Pan) {
      this.computeExtremePoint(true);
      this.computeExtremePoint(false);
    }

    // 计算墙体变化
    this.computeWallChange();
  }

  private computeExtremePoint(isStart = true) {
    const mainSeg = this._mainSeg;
    const mainMovedSeg = this._mainMovedSeg;
    const mainMovedShape = this._mainMovedShape;
    const relativeWallInfo = this._relativeWallInfo;
    if (!mainSeg || !mainMovedSeg || !mainMovedShape || !relativeWallInfo) return;

    const pointPointConnectedWallInfos = isStart
      ? relativeWallInfo.mainStartConnectToPoint
      : relativeWallInfo.mainEndConnectToPoint;
    const pointWallConnectedWallInfos = isStart
      ? relativeWallInfo.mainStartOnWallInternal
      : relativeWallInfo.mainEndOnWallInternal;
    const pointConnectedWallInfos = [...pointPointConnectedWallInfos, ...pointWallConnectedWallInfos];

    // -------------------------------- 仅与一堵墙体端点相连 --------------------------------
    if (pointPointConnectedWallInfos.length === 1 && pointWallConnectedWallInfos.length === 0) {
      const connectedWallInfo = pointPointConnectedWallInfos[0];
      const intersectPoint = getIntersectPoint({
        mainSeg,
        mainConnectType: isStart ? EPointOnSegmentType.StartPoint : EPointOnSegmentType.EndPoint,
        mainMovedSeg,
        mainMovedShape,
        subSeg: connectedWallInfo.seg,
        subShape: connectedWallInfo.shape,
        subConnectType: connectedWallInfo.connectType,
        operateType: this._operateData.type
      });

      if (!intersectPoint) return;

      this._result[isStart ? 'start' : 'end'] = {
        intersectPoint,
        wallId: pointPointConnectedWallInfos[0].id,
        intersectPointType: pointPointConnectedWallInfos[0].connectType
      };
      return;
    }

    // -------------------------------- 端点处在关联墙体上 --------------------------------
    if (pointConnectedWallInfos.length > 0) {
      let minMeasureValue = Infinity;
      let wallId;
      let intersectPoint;

      for (const info of pointConnectedWallInfos) {
        const _intersectPoint = getIntersectPoint({
          mainSeg,
          mainConnectType: isStart ? EPointOnSegmentType.StartPoint : EPointOnSegmentType.EndPoint,
          mainMovedSeg,
          mainMovedShape,
          subSeg: info.seg,
          subShape: info.shape,
          subConnectType: info.connectType,
          operateType: this._operateData.type
        });

        if (!_intersectPoint) continue;

        // 此分支的结果语义是"主墙端点滑到关联墙身上"（Between），不会修改关联墙几何。
        // 交点必须落在关联墙实际线段上；落在延长线/圆延伸上时该墙不可作为吸附目标（无墙可延展，应脱离）。
        if (!pointOnSeg(info.seg, _intersectPoint)) continue;

        if (pointConnectedWallInfos.length === 1) {
          wallId = info.id;
          intersectPoint = _intersectPoint;
          break;
        }

        const measureValue = getMeasureValue(_intersectPoint, mainMovedSeg, !isStart);
        if (!isNumber(measureValue)) continue;

        if (measureValue < minMeasureValue) {
          minMeasureValue = measureValue;
          wallId = info.id;
          intersectPoint = _intersectPoint;
        }
      }

      if (wallId && intersectPoint) {
        this._result[isStart ? 'start' : 'end'] = {
          intersectPoint,
          wallId,
          intersectPointType: EPointOnSegmentType.Between
        };
        return;
      }
    }

    // -------------------------------- 端点超出墙体，计算其他间接关联墙体 --------------------------------
    // if (mainPointOnWallFirst) {
    //   let wallId;
    //   let minDistance = Infinity;
    //   let intersectPoint;

    //   const pointMap = this._pointMap!;
    //   const _startPoint = mainPointOnWallFirst.seg.start;
    //   const _endPoint = mainPointOnWallFirst.seg.end;
    //   const _startPointDistance = pointToShapeDistance(mainMovedLine, _startPoint);
    //   const _endPointDistance = pointToShapeDistance(mainMovedLine, _endPoint);
    //   const _nearestPoint = _startPointDistance < _endPointDistance ? _startPoint : _endPoint;

    //   // 从相连的端点去找邻近墙体
    //   const nearestNeighborWallIds = pointMap.get(`${Point2dUtils.toString(_nearestPoint)}`) || [];

    //   for (const neighborWallId of nearestNeighborWallIds) {
    //     const wallData = this._wallStore.getUniformWallData(neighborWallId)!;
    //     const wallSeg = new LineSeg2d(wallData.baseData.start, wallData.baseData.end);
    //     const wallLine = new Line2d({
    //       points: { p1: wallData.baseData.start, p2: wallData.baseData.end }
    //     });

    //     const ps = intersectCommonShape(mainMovedLine, wallLine);
    //     const _intersectPoint = ps ? ps[0] : null; // TODO: 待修复，处理多交点情况
    //     if (_intersectPoint && wallSeg.pointOnSegment(_intersectPoint)) {
    //       const distance = Point2dUtils.distanceTo(oppositePoint, _intersectPoint);
    //       if (distance < minDistance) {
    //         minDistance = distance;
    //         wallId = neighborWallId;
    //         intersectPoint = _intersectPoint;
    //       }
    //     }
    //   }

    //   if (minDistance < Infinity) {
    //     this._result[isStart ? 'start' : 'end'] = {
    //       intersectPoint: intersectPoint!,
    //       wallId: wallId!,
    //       intersectPointType: EPointOnSegmentType.Between
    //     };

    //     return;
    //   }
    // }
  }

  private computeWallChange() {
    const mainStartResult = this._result.start;
    const mainEndResult = this._result.end;
    const mainCenter = this._result.center;
    const mainClockwise = this._result.clockwise;
    const mainWallData = this._mainWallData;
    const relativeWallInfo = this._relativeWallInfo;
    const mainMovedShape = this._mainMovedShape;
    const mainSeg = this._mainSeg;

    if (!mainStartResult || !mainEndResult || !mainWallData || !relativeWallInfo || !mainMovedShape || !mainSeg) return;

    const otherRelativeWallInfos = [];

    // 记录修改的墙体信息
    const modifyWallMap: Record<string, IOriginModifyWallInfo> = {};
    function addModifyWallInfo(modifyWallInfo: IOriginModifyWallInfo) {
      if (modifyWallMap[modifyWallInfo.wallId]) {
        modifyWallMap[modifyWallInfo.wallId].updateBaseData = {
          ...modifyWallMap[modifyWallInfo.wallId].updateBaseData,
          ...modifyWallInfo.updateBaseData
        };
      } else {
        modifyWallMap[modifyWallInfo.wallId] = modifyWallInfo;
      }
    }

    // -------------------------------- 主墙体 --------------------------------
    const movedMainWallBaseData = {
      start: mainStartResult.intersectPoint,
      end: mainEndResult.intersectPoint,
      center: mainCenter,
      clockwise: mainClockwise
    };
    addModifyWallInfo({ wallId: mainWallData.id, updateBaseData: movedMainWallBaseData });

    // -------------------------------- 主墙体两端端点端点相连的墙体 --------------------------------
    if (mainStartResult.wallId) {
      switch (mainStartResult.intersectPointType) {
        case EPointOnSegmentType.StartPoint:
          addModifyWallInfo({
            wallId: mainStartResult.wallId,
            updateBaseData: {
              start: mainStartResult.intersectPoint
            }
          });
          break;
        case EPointOnSegmentType.EndPoint:
          addModifyWallInfo({
            wallId: mainStartResult.wallId,
            updateBaseData: {
              end: mainStartResult.intersectPoint
            }
          });
          break;
        case EPointOnSegmentType.Between:
          otherRelativeWallInfos.push({
            wallId: mainStartResult.wallId
          });
          break;
      }
    }
    if (mainEndResult.wallId) {
      switch (mainEndResult.intersectPointType) {
        case EPointOnSegmentType.StartPoint:
          addModifyWallInfo({
            wallId: mainEndResult.wallId,
            updateBaseData: {
              start: mainEndResult.intersectPoint
            }
          });
          break;
        case EPointOnSegmentType.EndPoint:
          addModifyWallInfo({
            wallId: mainEndResult.wallId,
            updateBaseData: {
              end: mainEndResult.intersectPoint
            }
          });
          break;
        case EPointOnSegmentType.Between:
          otherRelativeWallInfos.push({
            wallId: mainEndResult.wallId
          });
          break;
      }
    }

    // -------------------------------- 端点在主墙体上的墙体 --------------------------------
    const pointOnMainWall = [
      ...relativeWallInfo.startPointOnMainWallInternal,
      ...relativeWallInfo.endPointOnMainWallInternal
    ];

    // 主墙体移动后的数据
    const movedMainWallSeg = getShapeSeg(movedMainWallBaseData);
    for (const wallInfo of pointOnMainWall) {
      const intersectPoint = getIntersectPoint({
        mainSeg,
        mainConnectType: EPointOnSegmentType.Between,
        mainMovedSeg: movedMainWallSeg,
        mainMovedShape,
        subSeg: wallInfo.seg,
        subShape: wallInfo.shape,
        subConnectType: wallInfo.connectType,
        operateType: this._operateData.type
      });

      if (!intersectPoint) continue;

      switch (wallInfo.connectType) {
        case EPointOnSegmentType.StartPoint:
          addModifyWallInfo({
            wallId: wallInfo.id,
            updateBaseData: {
              start: intersectPoint
            }
          });
          break;
        case EPointOnSegmentType.EndPoint:
          addModifyWallInfo({
            wallId: wallInfo.id,
            updateBaseData: {
              end: intersectPoint
            }
          });
          break;
        case EPointOnSegmentType.Between:
          otherRelativeWallInfos.push({
            wallId: wallInfo.id
          });
          break;
      }
    }

    this._result.modifyWallInfos = Object.values(modifyWallMap);
    this._result.otherRelativeWallInfos = otherRelativeWallInfos.filter((info) => !modifyWallMap[info.wallId]);
  }
}

export function pointToWallDistance(point: IPoint2d, wallData: IUniformWallData) {
  let moveDistance = 0;
  if (wallData) {
    const startPoint = Point2dUtils.clone(wallData.start);
    const endPoint = Point2dUtils.clone(wallData.end);
    const flag = Math.sign(new LineSeg2d(startPoint, endPoint).pointLineSegSide(point));
    const wallLine = new Line2d({ points: { p1: startPoint, p2: endPoint } });
    moveDistance = wallLine.pointToLineDistance(point) * flag;
  }

  return moveDistance;
}

// 计算出度量值最大和最小的交点，从起点/终点开始计算
function calculateMinAndMaxMeasurePoint(
  intersectPoints: IPoint2d[],
  referenceShape: LineSeg2d | ArcSeg2d,
  fromStart: boolean
) {
  if (intersectPoints.length < 2) {
    console.error('intersectPoints length must be greater than 2');
    return;
  }

  let minCache = Infinity;
  let minPoint: IPoint2d | null = null;
  let maxCache = -Infinity;
  let maxPoint: IPoint2d | null = null;

  if (referenceShape instanceof LineSeg2d) {
    const ray = fromStart ? referenceShape.createStartRay() : referenceShape.createEndRay();
    intersectPoints.forEach((p) => {
      if (!ray.pointOnRay(p)) return; //  默认要在射线上
      const measureValue = getMeasureValue(p, referenceShape, fromStart);
      if (!isNumber(measureValue)) return;

      if (measureValue < minCache) {
        minCache = measureValue;
        minPoint = p;
      }
      if (measureValue > maxCache) {
        maxCache = measureValue;
        maxPoint = p;
      }
    });
  } else if (referenceShape instanceof ArcSeg2d) {
    intersectPoints.forEach((p) => {
      const measureValue = getMeasureValue(p, referenceShape, fromStart);
      if (!isNumber(measureValue)) return;

      if (measureValue < minCache) {
        minCache = measureValue;
        minPoint = p;
      }
      if (measureValue > maxCache) {
        maxCache = measureValue;
        maxPoint = p;
      }
    });
  }

  if (minPoint && maxPoint) {
    return [minPoint, maxPoint];
  }
}

function getIntersectPoint(params: {
  mainSeg: IShapeSeg2d;
  mainMovedSeg: IShapeSeg2d;
  mainMovedShape: IShape2d;
  mainConnectType: EPointOnSegmentType;
  subSeg: IShapeSeg2d;
  subConnectType: EPointOnSegmentType;
  subShape: IShape2d;
  operateType: EWallOperateType;
}) {
  const { mainSeg, mainConnectType, mainMovedSeg, mainMovedShape, subSeg, subConnectType, subShape } = params;

  const ps = intersectCommonShape(mainMovedShape, subShape);
  if (!ps) return;

  // // 两条直线相交的情况，特殊处理，直接返回
  const isTwoLineSeg = mainSeg instanceof LineSeg2d && subSeg instanceof LineSeg2d;
  if (isTwoLineSeg) {
    if (mainConnectType === EPointOnSegmentType.Between) {
      //  需要交点在主线段上
      return pointOnSeg(mainMovedSeg, ps[0]) ? ps[0] : null;
    } else if (subConnectType === EPointOnSegmentType.Between) {
      //  需要交点在辅线段上
      return pointOnSeg(subSeg, ps[0]) ? ps[0] : null;
    }

    return ps[0];
  }

  // 相切：直线与圆只有一个交点（如竖直邻墙与半圆弧墙在端点处相切）。
  // 此时无需近远选择，直接使用该切点，但需确认切点落在移动后的主段/辅段上，
  // 避免取到圆另一侧的切点。否则相邻墙端点无法更新，导致邻墙不伸缩。
  if (ps.length === 1) {
    const tangentPoint = ps[0];
    if (mainConnectType === EPointOnSegmentType.Between) {
      return pointOnSeg(mainMovedSeg, tangentPoint) ? tangentPoint : null;
    }
    if (subConnectType === EPointOnSegmentType.Between) {
      return pointOnSeg(subSeg, tangentPoint) ? tangentPoint : null;
    }
    // 端点相连：切点需落在移动后的主段上
    return pointOnSeg(mainMovedSeg, tangentPoint) ? tangentPoint : null;
  }

  // 其他非 2 交点情况不处理
  if (ps.length !== 2) return;

  // ================== 处理不同情况下的交点选择 ==================

  let _mainSeg = mainSeg;
  let _mainConnectType = mainConnectType;
  let _mainMovedSeg = mainMovedSeg;
  const _subSeg = subSeg;
  const _subConnectType = subConnectType;

  // mainSeg线段中间有交点的话，需要构建新的线段，交点作为新终点，方向不变
  if (mainConnectType === EPointOnSegmentType.Between) {
    // 移动后，交点关系发生变化，不处理
    const prevIntersectPoints = intersectCommonShape(mainSeg, subSeg);
    const prevIntersectPointCount = prevIntersectPoints?.length || 0;
    const psOnMainMovedSeg = ps.filter((p) => pointOnSeg(mainMovedSeg, p));
    const currentIntersectPointCount = psOnMainMovedSeg?.length || 0;
    if (prevIntersectPointCount === 2 && currentIntersectPointCount !== 2) return;

    const newEndPoint = _subConnectType === EPointOnSegmentType.StartPoint ? _subSeg.start : _subSeg.end;
    const originalLength = _mainSeg.length;

    if (_mainSeg instanceof ArcSeg2d) {
      _mainSeg = new ArcSeg2d({
        start: _mainSeg.start,
        end: newEndPoint,
        center: _mainSeg.center,
        clockwise: _mainSeg.clockwise
      });
    } else {
      _mainSeg = new LineSeg2d(_mainSeg.start, newEndPoint);
    }
    _mainConnectType = EPointOnSegmentType.EndPoint;

    // 处理下_mainMovedSeg
    const ratio = _mainSeg.length / originalLength;
    if (_mainMovedSeg instanceof ArcSeg2d) {
      const endAngle = normalizeAngle(
        _mainMovedSeg.startAngle + _mainMovedSeg.getSweepAngle() * ratio * (_mainMovedSeg.clockwise ? -1 : 1)
      );
      _mainMovedSeg = new ArcSeg2d({
        startAngle: _mainMovedSeg.startAngle,
        endAngle,
        center: _mainMovedSeg.center,
        radius: _mainMovedSeg.radius,
        clockwise: _mainMovedSeg.clockwise
      });
    } else {
      const movedEndPoint = Point2dUtils.addDistance(
        _mainMovedSeg.start,
        ratio * _mainMovedSeg.length,
        _mainMovedSeg.direction
      );
      _mainMovedSeg = new LineSeg2d(_mainMovedSeg.start, movedEndPoint);
    }
  }

  // 如果另一个端点在辅线段上，则需要构建新的弧线
  const oppositePoint = _mainConnectType === EPointOnSegmentType.StartPoint ? _mainSeg.end : _mainSeg.start;
  const isOppositePointOnSubSeg = pointOnSeg(_subSeg, oppositePoint);
  if (isOppositePointOnSubSeg && _mainSeg instanceof ArcSeg2d) {
    const arcSeg = _mainSeg;
    const startAngle = arcSeg.startAngle;
    const endAngle = arcSeg.endAngle;
    const sweepAngle = arcSeg.getSweepAngle();
    const clockwise = arcSeg.clockwise;
    const flag = clockwise ? -1 : 1;
    const movedSeg = _mainMovedSeg as ArcSeg2d;
    if (_mainConnectType === EPointOnSegmentType.StartPoint) {
      const newEndAngle = normalizeAngle(startAngle + (sweepAngle / 2) * flag);
      _mainSeg = new ArcSeg2d({
        startAngle,
        endAngle: newEndAngle,
        center: arcSeg.center,
        radius: arcSeg.radius,
        clockwise: clockwise
      });

      const newMovedEndAngle = normalizeAngle(movedSeg.startAngle + (movedSeg.getSweepAngle() / 2) * flag);
      _mainMovedSeg = new ArcSeg2d({
        startAngle: movedSeg.startAngle,
        endAngle: newMovedEndAngle,
        center: movedSeg.center,
        radius: movedSeg.radius,
        clockwise: movedSeg.clockwise
      });
    } else if (_mainConnectType === EPointOnSegmentType.EndPoint) {
      const newStartAngle = normalizeAngle(endAngle - (sweepAngle / 2) * flag);
      _mainSeg = new ArcSeg2d({
        startAngle: newStartAngle,
        endAngle,
        center: arcSeg.center,
        radius: arcSeg.radius,
        clockwise: clockwise
      });

      const newMovedStartAngle = normalizeAngle(movedSeg.endAngle - (movedSeg.getSweepAngle() / 2) * flag);
      _mainMovedSeg = new ArcSeg2d({
        startAngle: newMovedStartAngle,
        endAngle: movedSeg.endAngle,
        center: movedSeg.center,
        radius: movedSeg.radius,
        clockwise: movedSeg.clockwise
      });
    }
  } else if (isOppositePointOnSubSeg && _mainSeg instanceof LineSeg2d) {
    const startPoint = _mainSeg.start;
    const endPoint = _mainSeg.end;
    const midPoint = Point2dUtils.getCenter(startPoint, endPoint);
    if (_mainConnectType === EPointOnSegmentType.StartPoint) {
      _mainSeg = new LineSeg2d(startPoint, midPoint);
      _mainMovedSeg = new LineSeg2d(
        _mainMovedSeg.start,
        Point2dUtils.getCenter(_mainMovedSeg.start, _mainMovedSeg.end)
      );
    } else if (_mainConnectType === EPointOnSegmentType.EndPoint) {
      _mainSeg = new LineSeg2d(midPoint, endPoint);
      _mainMovedSeg = new LineSeg2d(Point2dUtils.getCenter(_mainMovedSeg.start, _mainMovedSeg.end), _mainMovedSeg.end);
    }
  }

  let distanceType = getIntersectPointDistanceType({
    mainSeg: _mainSeg,
    mainConnectType: _mainConnectType as EPointOnSegmentType.StartPoint | EPointOnSegmentType.EndPoint,
    subSeg: _subSeg
  });

  const minMaxMeasurePoint = calculateMinAndMaxMeasurePoint(
    ps,
    _mainMovedSeg,
    _mainConnectType === EPointOnSegmentType.EndPoint
  );
  if (!minMaxMeasurePoint) return;

  // ================== 可能需要进行近远交点选择的反转 ==================
  {
    let currentPoint;
    let nextPoint;

    switch (_mainConnectType) {
      case EPointOnSegmentType.StartPoint:
        currentPoint = _mainSeg.end;
        nextPoint = _mainMovedSeg.end;
        break;
      case EPointOnSegmentType.EndPoint:
        currentPoint = _mainSeg.start;
        nextPoint = _mainMovedSeg.start;
        break;
    }

    if (currentPoint && nextPoint) {
      // 退化情形：对侧端点恰好落在辅墙所在直线/圆上（如弧墙一端与共线直墙相连，另一端点在该直线上）。
      // 此时原始交点即对侧端点，近远分类与换侧反转（side 恒为 0）都无法区分平移方向，
      // 直接取距移动后连接端点最近的交点，保证连接端点的连续跟踪。
      const isOppositeOnSubShape =
        _subSeg instanceof ArcSeg2d
          ? Math.abs(Point2dUtils.distanceTo(_subSeg.center, currentPoint) - _subSeg.radius) < effectiveDistance
          : _subSeg.createLine().pointToLineDistance(currentPoint) < effectiveDistance;

      if (isOppositeOnSubShape) {
        const movedConnectPoint =
          _mainConnectType === EPointOnSegmentType.StartPoint ? _mainMovedSeg.start : _mainMovedSeg.end;
        const nearDistance = Point2dUtils.distanceTo(minMaxMeasurePoint[0], movedConnectPoint);
        const farDistance = Point2dUtils.distanceTo(minMaxMeasurePoint[1], movedConnectPoint);
        distanceType = nearDistance <= farDistance ? PointDistanceType.Near : PointDistanceType.Far;
      } else {
        if (_subSeg instanceof ArcSeg2d) {
          // 圆弧在内外圆切换时需要反转一次
          const circle = _subSeg.createCircle();
          const isCurrentInCircle = circle.isPointInCircle(currentPoint, true);
          const isNextInCircle = circle.isPointInCircle(nextPoint, true);
          if (isCurrentInCircle !== isNextInCircle) {
            distanceType = getOppositeDistanceType(distanceType);
          }
        } else if (_subSeg instanceof LineSeg2d) {
          // 直线在换侧时，需要反转一次
          const currentSide = Math.sign(_subSeg.pointLineSegSide(currentPoint));
          const nextSide = Math.sign(_subSeg.pointLineSegSide(nextPoint));
          if (currentSide * nextSide < 0) {
            distanceType = getOppositeDistanceType(distanceType);
          }
        }

        if (mainConnectType === EPointOnSegmentType.Between) {
          // 连接点关系变化，需要反转一次
          const isCurrentPointOnSubSeg = pointOnSeg(_subSeg, currentPoint);
          const isNextPointOnSubSeg = pointOnSeg(_subSeg, nextPoint);
          if (isCurrentPointOnSubSeg !== isNextPointOnSubSeg) {
            distanceType = getOppositeDistanceType(distanceType);
          }
        }
      }
    }
  }

  // 选择交点
  const intersectPoint = distanceType === PointDistanceType.Near ? minMaxMeasurePoint[0] : minMaxMeasurePoint[1];

  // 再次判断交点是否在主线段或辅线段上
  if (mainConnectType === EPointOnSegmentType.Between) {
    return pointOnSeg(mainMovedSeg, intersectPoint) ? intersectPoint : null;
  } else if (subConnectType === EPointOnSegmentType.Between) {
    return pointOnSeg(subSeg, intersectPoint) ? intersectPoint : null;
  }

  return intersectPoint;
}

// 判断交点是近交点还是远交点
function getIntersectPointDistanceType(params: {
  mainSeg: IShapeSeg2d;
  mainConnectType: EPointOnSegmentType.StartPoint | EPointOnSegmentType.EndPoint;
  subSeg: IShapeSeg2d;
}) {
  const { mainSeg, mainConnectType, subSeg } = params;

  const mainShape = mainSeg instanceof LineSeg2d ? mainSeg.createLine() : mainSeg.createCircle();
  const subShape = subSeg instanceof LineSeg2d ? subSeg.createLine() : subSeg.createCircle();

  const intersectPoints = intersectCommonShape(mainShape, subShape);
  if (!intersectPoints) return PointDistanceType.Near;
  if (intersectPoints.length === 1) return PointDistanceType.Near;

  // 需要对比的端点
  const comparePoint = mainConnectType === EPointOnSegmentType.StartPoint ? mainSeg.start : mainSeg.end;
  // 另一个端点
  const oppositePoint = mainConnectType === EPointOnSegmentType.StartPoint ? mainSeg.end : mainSeg.start;

  if (
    Point2dUtils.equals(oppositePoint, intersectPoints[0]) ||
    Point2dUtils.equals(oppositePoint, intersectPoints[1])
  ) {
    return PointDistanceType.Near;
  }

  const minMaxMeasurePoint = calculateMinAndMaxMeasurePoint(
    intersectPoints,
    mainSeg,
    mainConnectType === EPointOnSegmentType.EndPoint
  );
  if (!minMaxMeasurePoint) return PointDistanceType.Near;

  if (Point2dUtils.equals(minMaxMeasurePoint[0], comparePoint)) return PointDistanceType.Near;
  if (Point2dUtils.equals(minMaxMeasurePoint[1], comparePoint)) return PointDistanceType.Far;

  return PointDistanceType.Near;
}

function getMeasureValue(
  point: IPoint2d,
  referenceShape: LineSeg2d | ArcSeg2d,
  fromStart: boolean
): number | undefined {
  if (referenceShape instanceof LineSeg2d) {
    const ray = fromStart ? referenceShape.createStartRay() : referenceShape.createEndRay();
    if (!ray.pointOnRay(point)) return; //  默认要在射线上
    return Point2dUtils.distanceTo(ray.origin, point);
  } else if (referenceShape instanceof ArcSeg2d) {
    let measureValue = referenceShape.getPointSweepAngle(point);
    if (!fromStart) measureValue = normalizeAngle(referenceShape.getSweepAngle() - measureValue);
    return measureValue;
  }
}

function getOppositeDistanceType(distanceType: PointDistanceType) {
  return distanceType === PointDistanceType.Near ? PointDistanceType.Far : PointDistanceType.Near;
}
